;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import { useState, useEffect } from 'react';
import { Button, InputGroupText, Label, ModalBody, ModalFooter, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { getCandidate, saveCandidate } from '../../../api/candidate';
import { useUser } from '../../../contexts/UserContext';
import { logSentryError } from '../../../utils/logSentry';
import FormAlert from '../../forms/FormAlert';
import { FormikInput } from '../../forms/inputs';
import { getI18nUtils } from '../../../i18n/i18n-utils';
import { Dictionaries } from '../../../i18n/i18n-config';

export const validationSchema = yup.object({
  // wordt met parseInt verstuurd; API eist > 0
  maxDistance: yup.string().test('positive', 'input_invalid', (value) => !value || parseInt(value, 10) > 0),
  postalCode: yup
    .string()
    .when('maxDistance', ([maxDistance], schema) => (maxDistance ? schema.min(4).required('input-required') : schema.min(0))),
  city: yup
    .string()
    .when('maxDistance', ([maxDistance], schema) => (maxDistance ? schema.min(2).required('input-required') : schema.min(0))),
});

type JobPreferenceInputs = yup.InferType<typeof validationSchema>;

const initialValues: JobPreferenceInputs = { maxDistance: '', postalCode: '', city: '' };

type Props = {
  dictionary: Partial<Dictionaries>;
  next: () => void;
  prev: () => void;
};
function ProfileModalMaxDistance({ next, prev, dictionary }: Props) {
  const { formatMessage } = getI18nUtils(dictionary);
  const { candidate, setCandidate } = useUser();
  const [alert, setAlert] = useState<{
    type: 'success' | 'warning' | 'danger';
    message: string;
  } | null>(null);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (val, actions) => {
      try {
        const data: any = {
          jobPreferences: {
            maxDistance: val.maxDistance ? parseInt(val.maxDistance, 10) : null,
          },
        };
        if (val.postalCode) data.postalCode = val.postalCode.toString();
        if (val.city) data.city = val.city;
        await saveCandidate(data, 'acquaintance');
        // "silent" refresh of context
        const c = await getCandidate();
        setCandidate(c);
        actions.setSubmitting(false);
        actions.resetForm();
        next();
      } catch (e: any) {
        setAlert({ type: 'danger', message: 'internal-server-error' });
        actions.setSubmitting(false);
        logSentryError(e);
      }
    },
  });

  useEffect(() => {
    const { maxDistance } = candidate?.jobPreferences || {};
    formik.setValues({
      maxDistance: maxDistance ? maxDistance.toString() : initialValues.maxDistance,
      postalCode: candidate?.postalCode || initialValues.postalCode,
      city: candidate?.city || initialValues.city,
    });
  }, [candidate]);

  const { isSubmitting, handleSubmit, dirty, values } = formik;

  return (
    <>
      <ModalBody>
        <div className="py-4">
          <h2 className="d-block mb-3">{formatMessage({ id: 'profile_form_distance_title' })}</h2>
          <p>{formatMessage({ id: 'profile_form_distance_intro' })}</p>

          <fieldset disabled={isSubmitting} aria-busy={isSubmitting}>
            <div className="mb-3">
              <Label for="maxDistance">{formatMessage({ id: 'candidate_expectations_maxDistance' })}</Label>
              <div className="input-group d-flex">
                <FormikInput
                  dictionary={dictionary}
                  withoutLabel
                  formik={formik}
                  type="number"
                  name="maxDistance"
                  className="flex-grow-1 delete-right-radius"
                />
                <InputGroupText>KM</InputGroupText>
              </div>
            </div>

            <Row>
              <FormikInput
                dictionary={dictionary}
                className="col-lg-4 mb-3"
                required={!!values.maxDistance}
                name="postalCode"
                intlKey="user-postalCode"
                type="text"
                formik={formik}
              />
              <FormikInput
                dictionary={dictionary}
                className="col-lg-8 mb-3"
                required={!!values.maxDistance}
                name="city"
                intlKey="user-city"
                type="text"
                formik={formik}
              />
            </Row>
          </fieldset>
        </div>
      </ModalBody>
      <ModalFooter>
        <FormAlert dictionary={dictionary} className="flex-grow-1 mb-2" alert={alert} />
        <Button
          disabled={isSubmitting}
          color="primary"
          className="mt-5 w-100"
          onClick={() => {
            if (!dirty) next();
            else handleSubmit();
          }}
        >
          {formatMessage({ id: 'next' })}
        </Button>
        <Button disabled={isSubmitting} color="white" className="mt-3 mx-auto d-block" onClick={() => prev()}>
          {formatMessage({ id: 'previous' })}
        </Button>
      </ModalFooter>
    </>
  );
}

export default ProfileModalMaxDistance;
